import React, { useRef, useState } from 'react';
import { Modal, Box, IconButton, Button, Stack, CircularProgress, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';

interface ImagePreviewWithActionsProps {
  open: boolean;
  imageUrl: string;
  onClose: () => void;
  onDelete?: () => Promise<void>;
  onChange?: (file: File) => Promise<void>;
  photoType?: 'facePicture' | 'publicPicture';
}

const ImagePreviewWithActions: React.FC<ImagePreviewWithActionsProps> = ({ 
  open, 
  imageUrl, 
  onClose,
  onDelete,
  onChange,
  photoType
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isChanging, setIsChanging] = useState(false);

  const handleDelete = async () => {
    if (!onDelete) return;
    
    if (window.confirm(`Are you sure you want to delete this ${photoType === 'facePicture' ? 'face' : 'public'} picture?`)) {
      setIsDeleting(true);
      try {
        await onDelete();
        onClose();
      } catch (error) {
        console.error('Error deleting photo:', error);
        alert('Failed to delete photo');
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !onChange) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }

    setIsChanging(true);
    try {
      await onChange(file);
      onClose();
    } catch (error) {
      console.error('Error changing photo:', error);
      alert('Failed to change photo');
    } finally {
      setIsChanging(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleChangeClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />
      <Modal
        open={open}
        onClose={onClose}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'rgba(0, 0, 0, 0)',
          backdropFilter: 'none',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            maxWidth: '90vw',
            maxHeight: '90vh',
            bgcolor: 'rgba(255, 255, 255, 0.95)',
            borderRadius: 2,
            boxShadow: 3,
            p: 1,
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 1,
            }}
          >
            {onChange && (
              <Button
                variant="contained"
                size="small"
                startIcon={isChanging ? <CircularProgress size={16} /> : <PhotoCameraIcon />}
                onClick={handleChangeClick}
                disabled={isChanging || isDeleting}
              >
                Change
              </Button>
            )}
            {onDelete && (
              <Button
                variant="contained"
                color="error"
                size="small"
                startIcon={isDeleting ? <CircularProgress size={16} /> : <DeleteIcon />}
                onClick={handleDelete}
                disabled={isDeleting || isChanging}
              >
                Delete
              </Button>
            )}
            <IconButton
              onClick={onClose}
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.8)',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 1)',
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
          {imageUrl ? (
            <img
              src={imageUrl}
              alt="Full size preview"
              style={{
                maxWidth: '100%',
                maxHeight: '90vh',
                objectFit: 'contain',
                display: 'block',
              }}
            />
          ) : (
            <Box sx={{ p: 4, textAlign: 'center', minWidth: 300, minHeight: 200 }}>
              <Typography variant="body1" color="text.secondary">
                No image to display. Click "Change" to upload a new photo.
              </Typography>
            </Box>
          )}
        </Box>
      </Modal>
    </>
  );
};

export default ImagePreviewWithActions;
